import { useMelwaysInfo } from '@seek/melways-react';
import type { Market } from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import { Box, Checkbox, Stack } from 'braid-design-system';

import { useGetSuggestionsForCompanyLazyQuery } from 'src/graphql/Queries/getSuggestionsForCompany.generated';

import { DEFAULT_SUGGESTION_COUNT } from '../../constants';
import type { Suggestion } from '../../types';
import { FilterAccordion } from '../AccordionFilter/FilterAccordion';
import { Filter } from '../Filter/Filter';

import translations from './.vocab';

type CompanyFIlterProps = {
  expanded: boolean;
  selectedMarket: Market;
  selectedItems: Suggestion[];
  onCompanyChange: (newSelectedItems: Suggestion[]) => void;
  onExpandedToggle: (expanded: boolean) => void;
  matchLatestCompany: boolean;
  onMatchLatestCompanyChange: (newState: boolean) => void;
};

export const CompanyFilter = ({
  expanded,
  selectedMarket,
  selectedItems,
  onCompanyChange,
  onExpandedToggle,
  matchLatestCompany,
  onMatchLatestCompanyChange,
}: CompanyFIlterProps) => {
  const { t } = useTranslations(translations);
  const { language } = useMelwaysInfo();

  const [triggerGetSuggestions, { data }] =
    useGetSuggestionsForCompanyLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const suggestions = (data?.ontologyOrganisations || []).map(
    ({ id, normalisedText }) => ({
      id,
      name: normalisedText,
    }),
  );

  const handleSearchTextChange = (newSearchText: string) => {
    if (newSearchText) {
      triggerGetSuggestions({
        variables: {
          query: newSearchText,
          count: DEFAULT_SUGGESTION_COUNT,
          countryCode2: selectedMarket,
          languageCode: language,
          session: '',
        },
      });
    }
  };

  return (
    <FilterAccordion
      id="company-accordion-filter"
      expanded={expanded}
      label={t('COMPANY')}
      selectedItems={selectedItems}
      onToggle={onExpandedToggle}
    >
      <Stack space="small">
        <Filter
          id="company-filter"
          label=""
          placeholder={t('ENTER_COMPANY_NAME')}
          suggestions={suggestions}
          selectedItems={selectedItems}
          onDebouncedSearchTextChange={handleSearchTextChange}
          onChange={onCompanyChange}
        />
        <Box paddingLeft="xxsmall">
          <Checkbox
            size="small"
            id="company-filter-checkbox"
            label={t('MATCH_WITH_LATEST_COMPANY_ONLY')}
            checked={matchLatestCompany}
            onChange={() => onMatchLatestCompanyChange(!matchLatestCompany)}
          />
        </Box>
      </Stack>
    </FilterAccordion>
  );
};
